import {WebSocket, WebSocketServer} from 'ws';
import {
    ExtendedHttpIncomingMessage,
    ExtendedHttpServerResponse,
    ServerHttpRequestDecorator,
    ServerHttpResponseDecorator
} from './exchange.js';
import getLogger, {regexAwareReplacer} from '../logger.js';
import {acceptsOrigin} from './ws-client-verify.js';
import {createHandshakeInfo, SocketRoute} from './socket.js';
import {ServerHttpResponse, ServerWebExchange} from '../../types/web/server';
import {HttpStatus} from "../http/status.ts";
import {AsyncLocalStorage} from "node:async_hooks";

export class ExtendedWebSocket extends WebSocket {
    constructor(first: unknown, second?: unknown, options?: unknown) {
        super (null as unknown as any, undefined, options as any);
    }
    connected?: boolean;
}

const logger = getLogger('ws');


function upgradeStrategy(path: string, route: SocketRoute, wss: WebSocketServer, onSocketError) {
    return (exchange: ServerWebExchange) => {

        const {logPrefix, request} = exchange;
        const req = ServerHttpRequestDecorator.getNativeRequest<ExtendedHttpIncomingMessage>(request);
        req.exchange = exchange;
        const {socket, upgradeHead} = req;

        const host = request.host;
        socket.removeListener('error', onSocketError);
        if (route.maxConnections !== undefined && wss.clients?.size >= route.maxConnections) {
            logger.warn(`${logPrefix}dropping ws connection request on ${host}${path}. max connections exceeded.`);
            socket.destroy();
            return;
        }

        const origin = request.headers.one('origin');
        if (!acceptsOrigin(origin, route.originFilters)) {
            if (logger.enabledFor('info')) {
                logger.info(`${logPrefix}dropping ws connection request on ${host}${path}. origin ${origin ?? '<missing>'}`);
            }
            socket.destroy();
            return;
        }
        if (logger.enabledFor('debug')) {
            logger.debug(`${logPrefix}accepted new ws connection request on ${host}${path}`);
        }

        wss.handleUpgrade(req, socket, upgradeHead!, (client, req) => {
            wss.emit('connection', client, req);
        });
    }
}

function applyHandshakeHeaders(headers: string[], response: ServerHttpResponse) {
    const seen = new Set<string>(); // track headers we've taken care of
    headers.forEach((header, index) => {
        if (index === 0 && header.startsWith('HTTP/1.1 101 ')) {
            response.setStatusCode(HttpStatus.SWITCHING_PROTOCOLS);
            return;
        }
        const [name, value] = header.split(': ');
        if (response.headers.has(name)) {
            headers[index] = `${name}: ${response.headers.one(name)}`;
        } else {
            response.headers.set(name, value);
        }
        seen.add(name.toLowerCase());
    });
    const nativeResponse = ServerHttpResponseDecorator.getNativeResponse<ExtendedHttpServerResponse>(response);
    for (const name of nativeResponse.getRawHeaderNames()) {
        // if we have already set this header, skip it
        const nameLowerCase = name.toLowerCase();
        if (!seen.has(nameLowerCase)) {
            const value = response.headers.get(nameLowerCase);
            if (value !== undefined) {
                headers.push(`${name}: ${value}`);
            }
        }
    }
    nativeResponse.markHeadersSent();
}

export async function initRoute(path: string,
                                route: SocketRoute,
                                endpoint: string,
                                storage: AsyncLocalStorage<{securityContext?}>,
                                onSocketError): Promise<void> {
    try {
        logger.info(`creating ws server for [${path}]. max connections: ${route.maxConnections ?? '<unlimited>'}, origin filters: ${route.originFilters ? JSON.stringify(route.originFilters, regexAwareReplacer) : '<none>'}`);
        const wss = new WebSocketServer<typeof ExtendedWebSocket, typeof ExtendedHttpIncomingMessage>({
            noServer: true,
            WebSocket: ExtendedWebSocket,
            autoPong: false
        });

        const handler = await route.factory({endpoint, storage});
        wss
            .on('error', (err: Error) => {
                logger.error(`error starting the ws server for [${path}]`, err);
            })
            .on('listening', () => {
                logger.info(`ws server for [${path}] is listening`);
            })
            .on('headers', (headers, request) => {
                if (request.exchange !== undefined) {
                    const {response} = request.exchange;
                    applyHandshakeHeaders(headers, response);
                }
            })
            .on('connection', (socket, request) => {

                const handshake = createHandshakeInfo(request, socket.protocol);

                socket.on('pong', () => socket.connected = true);
                socket.on('ping', () => {
                });
                handler({socket, handshake});
            });
        const pingInterval = route.ping;
        if (pingInterval) {
            const pingIntervalId = setInterval(() => {
                for (const client of wss.clients) {
                    if (client.connected === false) {
                        client.terminate();
                    }
                    client.connected = false;
                    client.ping();
                }
            }, pingInterval);
            wss.on('close', () => {
                clearInterval(pingIntervalId);
            });
        }

        route.upgradeStrategy = upgradeStrategy(path, route, wss, onSocketError);
        route.close = async () => {
            await handler.close?.call(handler);
            logger.info(`stopping ws server for [${path}]. clients: ${wss.clients?.size ?? 0}`);
            wss.clients?.forEach(client => {
                client.terminate();
            });
            wss.close();
        }
    } catch (e) {
        logger.warn(`failed to init route ${path}`, e);
    }

}
